<template>
    <div class="cert-container" lang="zh">
        <div class="changeVersion" @click="changeVersion">
            前往旧版
        </div>
        <header class="topbar">
            <div class="brand-mark" aria-label="证书控制台">
                <span class="brand-symbol">CERT</span>
                <span class="brand-text">证书生成 Version{{ appVersion }}</span>
            </div>
        </header>

        <main class="page-shell">
            <section class="hero-section" :class="{ 'is-collapsed': heroCollapsed }" aria-labelledby="hero-title">
                <div class="hero-summary">
                    <button class="hero-toggle" type="button" :aria-expanded="(!heroCollapsed).toString()"
                            aria-controls="hero-details" @click="heroCollapsed = !heroCollapsed">
                        <span>{{ heroCollapsed ? '展开证书控制台' : '收起证书控制台' }}</span>
                        <span class="toggle-caret" aria-hidden="true"></span>
                    </button>
                </div>

                <div id="hero-details" v-show="!heroCollapsed" class="hero-details">
                    <div class="hero-details-grid">
                        <div class="hero-copy">
                            <p class="eyebrow">CERTIFICATE WORKSPACE</p>
                            <h1 id="hero-title">证书生成控制台</h1>
                            <p class="hero-desc">一个用于证书图片生成、电子版排版与二维码制作的轻量工作台，集中管理常用模板和生成入口。</p>
                        </div>
                        <aside class="hero-panel" aria-label="当前状态">
                            <div class="panel-note">
                                <span class="status-dot"></span>
                                <span>{{ activeCategoryLabel }} · {{ searchQuery || '全部模板' }}</span>
                            </div>
                            <div class="metric-row">
                                <div class="metric-item">
                                    <span class="metric-value">{{ generatedCount }}</span>
                                    <span class="metric-label">已生成预览</span>
                                </div>
                                <div class="metric-item">
                                    <span class="metric-value">{{ uploadedCount }}</span>
                                    <span class="metric-label">已上传照片</span>
                                </div>
                            </div>
                        </aside>
                    </div>

                    <section class="toolbar" aria-label="证书工具筛选">
                        <div class="search-control">
                            <span class="search-icon"></span>
                            <input v-model="searchQuery" type="search" placeholder="搜索 60、100、150、二维码"
                                   aria-label="搜索证书工具"/>
                        </div>
                        <div class="tab-list" role="tablist" aria-label="分类筛选">
                            <button type="button" :class="{ active: activeCategory === 'all' }"
                                    @click="activeCategory = 'all'">全部</button>
                            <button type="button" :class="{ active: activeCategory === 'paper' }"
                                    @click="activeCategory = 'paper'">纸质证书</button>
                            <button type="button" :class="{ active: activeCategory === 'digital' }"
                                    @click="activeCategory = 'digital'">电子版</button>
                            <button type="button" :class="{ active: activeCategory === 'qr' }"
                                    @click="activeCategory = 'qr'">二维码</button>
                        </div>
                    </section>
                </div>
            </section>

            <div class="workspace-grid" id="workspace">
                <div class="section-heading">
                    <p>Paper Certificate</p>
                    <h2>纸质证书制作</h2>
                </div>
                <div class="sections-container paper-grid">
                    <div class="cert-card featured-card"
                         v-show="cardVisible('paper', '60的证书制作 纸质证书')">
                        <div class="section-header">
                            <h3 class="section-title">60的证书制作</h3>
                        </div>

                        <div class="form-group">
                            <label for="name1">证书名</label>
                            <div class="input-row">
                                <input v-model="name1" type="text" id="name1" placeholder="请输入证书名" class="styled-input"
                                       aria-label="证书名输入框"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="number1">偏移量</label>
                            <div class="input-row">
                                <input v-model="number1" type="number" id="number1" placeholder="输入间距，3个字为810"
                                       class="styled-input" aria-label="偏移量输入框"/>
                            </div>
                        </div>

                        <button @click="submitSixtyCert" class="btn btn-green" aria-label="创建60的证书按钮">
                            创建60的证书
                        </button>

                        <div v-if="file1Img" class="image-preview-wrapper">
                            <img :src="file1Img" alt="60证书图片" class="preview-img" @click="openFullScreen(file1Img)"
                                 aria-label="查看60证书图片"/>
                        </div>
                    </div>

                    <div class="cert-card featured-card"
                         v-show="cardVisible('paper', '100的证书制作 纸质证书')">
                        <div class="section-header">
                            <h3 class="section-title">100的证书制作</h3>
                        </div>

                        <div class="form-group">
                            <label for="name2">证书名</label>
                            <div class="input-row">
                                <input v-model="name2" type="text" id="name2" placeholder="请输入证书名" class="styled-input"
                                       aria-label="证书名输入框"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="number2">偏移量</label>
                            <div class="input-row">
                                <input v-model="number2" type="number" id="number2" placeholder="输入间距，3个字为560"
                                       class="styled-input" aria-label="偏移量输入框"/>
                            </div>
                        </div>

                        <button @click="submitHundredCert" class="btn btn-purple" aria-label="创建100的证书按钮">
                            创建100的证书
                        </button>

                        <div v-if="file2Img" class="image-preview-wrapper">
                            <img :src="file2Img" alt="100证书图片" class="preview-img" @click="openFullScreen(file2Img)"
                                 aria-label="查看100证书图片"/>
                        </div>
                    </div>

                    <div class="cert-card featured-card"
                         v-show="cardVisible('paper', '150的证书制作 纸质证书')">
                        <div class="section-header">
                            <h3 class="section-title">150的证书制作</h3>
                        </div>

                        <div class="form-group">
                            <label for="name6">证书名</label>
                            <div class="input-row">
                                <input v-model="name6" type="text" id="name6" placeholder="请输入证书名" class="styled-input"
                                       aria-label="证书名输入框"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label for="number6">偏移量</label>
                            <div class="input-row">
                                <input v-model="number6" type="number" id="number6" placeholder="输入间距，3个字为560"
                                       class="styled-input" aria-label="偏移量输入框"/>
                            </div>
                        </div>

                        <button @click="submitHundredFiftyCert" class="btn btn-orange" aria-label="创建150的证书按钮">
                            创建150的证书
                        </button>

                        <div v-if="file6Img" class="image-preview-wrapper">
                            <img :src="file6Img" alt="150证书图片" class="preview-img" @click="openFullScreen(file6Img)"
                                 aria-label="查看150证书图片"/>
                        </div>
                    </div>
                </div>

                <div class="section-heading">
                    <p>Digital Workspace</p>
                    <h2>电子版与二维码</h2>
                </div>
                <div class="sections-container">
                    <div class="cert-card"
                         v-show="cardVisible('digital', '60电子版生成')">
                        <div class="section-header">
                            <h3 class="section-title">60电子版生成</h3>
                            <div class="toggle-wrapper">
                                <span class="toggle-label">位置微调</span>
                                <label class="modern-switch">
                                    <input type="checkbox" v-model="isShowPostion"/>
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="upload-label">上传照片</label>
                            <div class="upload-controls">
                                <input type="file" id="photo" @change="handleFileChange" class="hidden-file-input"
                                       data-source="E"/>
                                <label for="photo" class="btn-upload"><i class="icon">📁</i> 选择照片</label>
                            </div>
                            <div v-if="imagePreview" class="image-preview-wrapper small-preview">
                                <img :src="imagePreview" alt="Preview"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>姓名</label>
                            <div class="input-row">
                                <input v-model="userName" type="text" placeholder="输入姓名" class="styled-input"/>
                                <input v-model="userNameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>性别</label>
                            <div class="input-row">
                                <div class="gender-btn" :class="{'active': gender === '男'}" @click="gender = '男'">男</div>
                                <div class="gender-btn" :class="{'active': gender === '女'}" @click="gender = '女'">女</div>
                                <input v-model="genderXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>出生日期</label>
                            <div class="input-row">
                                <input v-model="birthDate" type="text" placeholder="2025.01.01" class="styled-input"/>
                                <input v-model="birthDateXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>身份证号</label>
                            <div class="input-row">
                                <input v-model="cardId" type="text" placeholder="身份证号" class="styled-input"/>
                                <input v-model="cardIdXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书名称</label>
                            <div class="input-row">
                                <input v-model="certName" type="text" placeholder="焊工、电工..." class="styled-input"/>
                                <input v-model="certNameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>级别</label>
                            <div class="input-row">
                                <input v-model="level" type="text" placeholder="高级、中级..." class="styled-input"/>
                                <input v-model="levelXLocation" type="number" class="styled-input pos-input" placeholder="X坐标"
                                       v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书号</label>
                            <div class="input-row">
                                <input v-model="certNo" type="text" :placeholder="getPatternTime('yyyyMMdd')"
                                       class="styled-input"/>
                                <input v-model="certNoXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>发证时间</label>
                            <div class="input-row date-row">
                                <input v-model="year" type="text" class="styled-input date-input"
                                       placeholder="年"/><span>年</span>
                                <input v-model="month" type="text" class="styled-input date-input"
                                       placeholder="月"/><span>月</span>
                                <input v-model="day" type="text" class="styled-input date-input"
                                       placeholder="日"/><span>日</span>
                            </div>
                        </div>

                        <button @click="submitESixtyCert" class="btn btn-cyan">
                            创建60的电子版
                        </button>

                        <div v-if="file3Img" class="image-preview-wrapper">
                            <img :src="file3Img" alt="结果图片" class="preview-img" @click="openFullScreen(file3Img)"/>
                        </div>
                    </div>

                    <div class="cert-card"
                         v-show="cardVisible('digital', '60电子版 长名字')">
                        <div class="section-header">
                            <h3 class="section-title">60电子版(长名字)</h3>
                            <div class="toggle-wrapper">
                                <span class="toggle-label">位置微调</span>
                                <label class="modern-switch">
                                    <input type="checkbox" v-model="e60isShowPostion"/>
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="upload-label">上传照片</label>
                            <div class="upload-controls">
                                <input type="file" id="photoe60" @change="handleFileChange" class="hidden-file-input"
                                       data-source="e60"/>
                                <label for="photoe60" class="btn-upload"><i class="icon">📁</i> 选择照片</label>
                            </div>
                            <div v-if="e60imagePreview" class="image-preview-wrapper small-preview">
                                <img :src="e60imagePreview" alt="Preview"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>姓名第一行(可不填)</label>
                            <div class="input-row">
                                <input v-model="e60userName" type="text" placeholder="输入姓" class="styled-input"/>
                                <input v-model="e60userNameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>姓名第二行(必填)</label>
                            <div class="input-row">
                                <input v-model="e60userName2" type="text" placeholder="输入名" class="styled-input"/>
                                <input v-model="e60userName2XLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>性别</label>
                            <div class="input-row">
                                <div class="gender-btn" :class="{'active': e60gender === '男'}" @click="e60gender = '男'">男
                                </div>
                                <div class="gender-btn" :class="{'active': e60gender === '女'}" @click="e60gender = '女'">女
                                </div>
                                <input v-model="e60genderXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>出生日期</label>
                            <div class="input-row">
                                <input v-model="e60birthDate" type="text" placeholder="2025.01.01" class="styled-input"/>
                                <input v-model="e60birthDateXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>身份证号</label>
                            <div class="input-row">
                                <input v-model="e60cardId" type="text" placeholder="身份证号" class="styled-input"/>
                                <input v-model="e60cardIdXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书名称第一行(可不填)</label>
                            <div class="input-row">
                                <input v-model="e60certName" type="text" placeholder="焊工、电工..." class="styled-input"/>
                                <input v-model="e60certNameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书名称第二行(必填)</label>
                            <div class="input-row">
                                <input v-model="e60certName2" type="text" placeholder="焊工、电工..." class="styled-input"/>
                                <input v-model="e60certName2XLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>级别</label>
                            <div class="input-row">
                                <input v-model="e60level" type="text" placeholder="高级、中级..." class="styled-input"/>
                                <input v-model="e60levelXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书号</label>
                            <div class="input-row">
                                <input v-model="e60certNo" type="text" :placeholder="getPatternTime('yyyyMMdd')"
                                       class="styled-input"/>
                                <input v-model="e60certNoXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="e60isShowPostion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>发证时间</label>
                            <div class="input-row date-row">
                                <input v-model="e60year" type="text" class="styled-input date-input"
                                       placeholder="年"/><span>年</span>
                                <input v-model="e60month" type="text" class="styled-input date-input"
                                       placeholder="月"/><span>月</span>
                                <input v-model="e60day" type="text" class="styled-input date-input"
                                       placeholder="日"/><span>日</span>
                            </div>
                        </div>

                        <button @click="submitESixtyCert2" class="btn btn-cyan">
                            创建60的电子版
                        </button>

                        <div v-if="e60file3Img" class="image-preview-wrapper">
                            <img :src="e60file3Img" alt="结果图片" class="preview-img" @click="openFullScreen(e60file3Img)"/>
                        </div>
                    </div>

                    <div class="cert-card relative-card"
                         v-show="cardVisible('digital', '100电子版生成')">
                        <div v-if="isLoading" class="loader-overlay">
                            <div class="spinner"></div>
                        </div>
                        <div class="section-header">
                            <h3 class="section-title">100电子版生成</h3>
                            <div class="toggle-wrapper">
                                <span class="toggle-label">位置微调</span>
                                <label class="modern-switch">
                                    <input type="checkbox" v-model="isShowE100Postion"/>
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="upload-label">上传照片</label>
                            <div class="upload-controls">
                                <input type="file" id="photoe100" @change="handleFileChange" class="hidden-file-input"
                                       data-source="e100"/>
                                <label for="photoe100" class="btn-upload"><i class="icon">📁</i> 选择照片</label>
                            </div>
                            <div v-if="imageE100Preview" class="image-preview-wrapper small-preview">
                                <img :src="imageE100Preview" alt="Preview"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>姓名</label>
                            <div class="input-row">
                                <input v-model="e100userName" type="text" placeholder="输入姓名" class="styled-input"/>
                                <input v-model="e100userNameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>性别</label>
                            <div class="input-row">
                                <div class="gender-btn" :class="{'active': e100gender === '男'}" @click="e100gender = '男'">男
                                </div>
                                <div class="gender-btn" :class="{'active': e100gender === '女'}" @click="e100gender = '女'">女
                                </div>
                                <input v-model="e100genderXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>身份证号</label>
                            <div class="input-row">
                                <input v-model="e100cardId" type="text" placeholder="身份证号" class="styled-input"/>
                                <input v-model="e100cardIdXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>出生日期</label>
                            <div class="input-row">
                                <input v-model="e100birthDate" type="text" :placeholder="getPatternTime('yyyy-MM-dd')"
                                       class="styled-input"/>
                                <input v-model="e100birthDateXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书编号</label>
                            <div class="input-row">
                                <input v-model="e100certNo" type="text" :placeholder="'JSAC'+getPatternTime('yyyyMMddHHmmss')"
                                       class="styled-input"/>
                                <input v-model="e100certNoXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>颁证日期</label>
                            <div class="input-row">
                                <input v-model="e100year" type="text" :placeholder="getPatternTime('yyyy-MM-dd')"
                                       class="styled-input"/>
                                <input v-model="e100yearXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书名称</label>
                            <div class="input-row">
                                <input v-model="e100certName" type="text" placeholder="焊工、电工..." class="styled-input"/>
                                <input v-model="e100certNameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>职业等级</label>
                            <div class="input-row">
                                <input v-model="e100level" type="text" placeholder="一级、二级..." class="styled-input"/>
                                <input v-model="e100levelXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>成绩</label>
                            <div class="input-row">
                                <span class="inline-label">理论:</span>
                                <input v-model="e100theoryScore" type="text" class="styled-input pos-input" placeholder="99"/>
                                <span class="inline-label">技能:</span>
                                <input v-model="e100assessScore" type="text" class="styled-input pos-input" placeholder="98"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>工作单位</label>
                            <div class="input-row">
                                <input v-model="e100workAddress" type="text" placeholder="工作单位地址" class="styled-input"/>
                                <input v-model="e100workAddressXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShowE100Postion"/>
                            </div>
                        </div>

                        <button @click="submitEHunderCert" class="btn btn-purple-dark">
                            创建100的电子版
                        </button>

                        <div v-if="e100fileImg" class="image-preview-wrapper">
                            <img :src="e100fileImg" alt="结果图片" class="preview-img" @click="openFullScreen(e100fileImg)"/>
                        </div>
                    </div>

                    <div class="cert-card"
                         v-show="cardVisible('digital', '150电子版制作')">
                        <div class="section-header">
                            <h3 class="section-title">150电子版制作</h3>
                            <div class="toggle-wrapper">
                                <span class="toggle-label">位置微调</span>
                                <label class="modern-switch">
                                    <input type="checkbox" v-model="isShow150Postion"/>
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>

                        <div class="form-group">
                            <label class="upload-label">上传照片</label>
                            <div class="upload-controls">
                                <input type="file" id="photo2" @change="handleFileChange" class="hidden-file-input"
                                       data-source="150"/>
                                <label for="photo2" class="btn-upload"><i class="icon">📁</i> 选择照片</label>
                            </div>
                            <div v-if="image150Preview" class="image-preview-wrapper small-preview">
                                <img :src="image150Preview" alt="Preview"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>姓名</label>
                            <div class="input-row">
                                <input v-model="user150Name" type="text" placeholder="输入姓名" class="styled-input"/>
                                <input v-model="user150NameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShow150Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>身份证号</label>
                            <div class="input-row">
                                <input v-model="cardId150" type="text" placeholder="身份证号" class="styled-input"/>
                                <input v-model="cardId150XLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShow150Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书名称</label>
                            <div class="input-row">
                                <input v-model="cert150Name" type="text" placeholder="焊工、电工..." class="styled-input"/>
                                <input v-model="cert150NameXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShow150Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>级别</label>
                            <div class="input-row">
                                <input v-model="level150" type="text" placeholder="高级、中级..." class="styled-input"/>
                                <input v-model="level150XLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShow150Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书号</label>
                            <div class="input-row">
                                <input v-model="cert150No" type="text" :placeholder="'ABCD'+getPatternTime('yyyyMMdd')+'EFG'"
                                       class="styled-input"/>
                                <input v-model="cert150NoXLocation" type="number" class="styled-input pos-input"
                                       placeholder="X坐标" v-if="isShow150Postion"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>发证时间</label>
                            <div class="input-row date-row">
                                <input v-model="year150" type="text" class="styled-input date-input"
                                       placeholder="年"/><span>年</span>
                                <input v-model="month150" type="text" class="styled-input date-input"
                                       placeholder="月"/><span>月</span>
                                <input v-model="day150" type="text" class="styled-input date-input"
                                       placeholder="日"/><span>日</span>
                            </div>
                        </div>

                        <button @click="submitEHundredFiftyCert" class="btn btn-dark">
                            创建150证书
                        </button>

                        <div v-if="file150Img" class="image-preview-wrapper">
                            <img :src="file150Img" alt="结果图片" class="preview-img" @click="openFullScreen(file150Img)"/>
                        </div>
                    </div>

                    <div class="cert-card qr-card"
                         v-show="cardVisible('qr', '二维码生成')">
                        <div class="section-header">
                            <h3 class="section-title">二维码生成</h3>
                        </div>

                        <div class="form-group">
                            <label class="upload-label">上传照片</label>
                            <div class="upload-controls">
                                <input type="file" id="photoQrCode" @change="handleFileChange" class="hidden-file-input"
                                       data-source="qrCode"/>
                                <label for="photoQrCode" class="btn-upload"><i class="icon">📁</i> 选择照片</label>
                            </div>
                            <div v-if="qrCode.imagePreview" class="image-preview-wrapper small-preview">
                                <img :src="qrCode.imagePreview" alt="Preview"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>姓名</label>
                            <div class="input-row">
                                <input v-model="qrCode.name" type="text" placeholder="请输入姓名" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>性别</label>
                            <div class="input-row">
                                <div class="gender-btn" :class="{'active': qrCode.gender === '男'}"
                                     @click="qrCode.gender = '男'">男
                                </div>
                                <div class="gender-btn" :class="{'active': qrCode.gender === '女'}"
                                     @click="qrCode.gender = '女'">女
                                </div>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>身份证号</label>
                            <div class="input-row">
                                <input v-model="qrCode.cardId" type="text" placeholder="身份证号" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>证书编号</label>
                            <div class="input-row">
                                <input v-model="qrCode.certCode" type="text" placeholder="证书编号" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>颁证日期</label>
                            <div class="input-row">
                                <input v-model="qrCode.generateDate" type="text" placeholder="颁证日期" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>职业名称</label>
                            <div class="input-row">
                                <input v-model="qrCode.jobName" type="text" placeholder="职业名称" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>职业等级</label>
                            <div class="input-row">
                                <input v-model="qrCode.jobLevel" type="text" placeholder="职业等级" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>理论知识考试成绩</label>
                            <div class="input-row">
                                <input v-model="qrCode.examScore" type="text" placeholder="成绩" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>技能考核成绩</label>
                            <div class="input-row">
                                <input v-model="qrCode.skillScore" type="text" placeholder="成绩" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>评定成绩</label>
                            <div class="input-row">
                                <input v-model="qrCode.confirmScore" type="text" placeholder="评定成绩" class="styled-input"/>
                            </div>
                        </div>

                        <div class="form-group">
                            <label>工作单位</label>
                            <div class="input-row">
                                <input v-model="qrCode.workAddress" type="text" placeholder="工作单位" class="styled-input"/>
                            </div>
                        </div>

                        <button @click="submitQrCode" class="btn btn-dark">
                            生成二维码
                        </button>

                        <div v-if="qrCode.qrCodeImage" class="image-preview-wrapper">
                            <img :src="qrCode.qrCodeImage" alt="二维码" class="preview-img"
                                 @click="openFullScreen(qrCode.qrCodeImage)"/>
                        </div>
                    </div>
                </div>
            </div>
        </main>

        <Notification :message="notificationMessage" :visible.sync="isNotificationVisible" :type="notificationType"
                      aria-label="通知组件"/>
    </div>
</template>

<script>
// JS 逻辑保持与您提供的一模一样，为节约篇幅且不破坏您的业务，未作改动
import {
    edit150PicSize, getESixtyCert, getHundredFiftyCert, getHundredCert, getEHundredFiftyCert, getSixtyCert,
    uploadPic, edit100PicSize, getEHundredCert, getESixtyCert2, getQrCode
} from "@/api/wmjApi";
import Notification from "@/components/Notification.vue";
import {isIDCard} from "@/utils/checkUtil";
import {getPatternTime} from "@/utils/parse";

const APP_VERSION = '2.1.0';
const APP_VERSION_LABEL = '2.1';
const APP_VERSION_STORAGE_KEY = 'cert_generator_version';

export default {
    components: {Notification},
    data() {
        return {
            appVersion: APP_VERSION_LABEL,
            searchQuery: '',
            activeCategory: 'all',
            heroCollapsed: true,
            qrCode: {
                imagePreview: null,
                name: '',
                gender: '',
                cardId: '',
                certCode: '',
                generateDate: '',
                jobName: '',
                jobLevel: '',
                examScore: '',
                skillScore: '',
                confirmScore: '',
                workAddress: '',
                selectedFile: null,
                photo: '',
                qrCodeImage: ''
            },
            name1: '',
            number1: 1450,
            file1Img: '',
            name2: '',
            number2: 1465,
            file2Img: '',
            userName: '',
            userName2: '',
            userNameXLocation: 350,
            userName2XLocation: 350,
            userNameYLocation: 1100,
            userName2YLocation: 1250,
            gender: '',
            genderXLocation: 1000,
            genderYLocation: 1200,
            birthDate: '',
            birthDateXLocation: 530,
            birthDateYLocation: 1500,
            cardId: '',
            cardIdXLocation: 510,
            cardIdYLocation: 1800,
            certName: '',
            certNameXLocation: 2300,
            certNameYLocation: 375,
            level: '',
            levelXLocation: 2250,
            levelYLocation: 680,
            certNo: '',
            certNoXLocation: 2300,
            certNoYLocation: 990,
            year: '',
            yearXLocation: 2180,
            yearYLocation: 1827,
            month: '',
            monthXLocation: 2450,
            monthYLocation: 1827,
            day: '',
            dayXLocation: 2650,
            dayYLocation: 1827,
            certType: '',
            picPath: '',
            file3Img: '',
            e60userName: '',
            e60userName2: '',
            e60userNameXLocation: 350,
            e60userName2XLocation: 350,
            e60userNameYLocation: 1165,
            e60userName2YLocation: 1250,
            e60gender: '',
            e60genderXLocation: 1000,
            e60genderYLocation: 1200,
            e60birthDate: '',
            e60birthDateXLocation: 530,
            e60birthDateYLocation: 1500,
            e60cardId: '',
            e60cardIdXLocation: 510,
            e60cardIdYLocation: 1800,
            e60certName: '',
            e60certNameXLocation: 2150,
            e60certNameYLocation: 335,
            e60certName2: '',
            e60certName2XLocation: 2150,
            e60certName2YLocation: 415,
            e60level: '',
            e60levelXLocation: 2250,
            e60levelYLocation: 680,
            e60certNo: '',
            e60certNoXLocation: 2300,
            e60certNoYLocation: 990,
            e60year: '',
            e60yearXLocation: 2180,
            e60yearYLocation: 1827,
            e60month: '',
            e60monthXLocation: 2450,
            e60monthYLocation: 1827,
            e60day: '',
            e60dayXLocation: 2650,
            e60dayYLocation: 1827,
            e60certType: '',
            e60picPath: '',
            e60file3Img: '',
            e60imagePreview: null,
            e60selectedFile: null,
            e60isShowPostion: false,
            user150Name: '',
            user150NameXLocation: 1185,
            user150NameYLocation: 570,
            cardId150: '',
            cardId150XLocation: 1135,
            cardId150YLocation: 652,
            cert150Name: '',
            cert150NameXLocation: 1185,
            cert150NameYLocation: 735,
            level150: '',
            level150XLocation: 1185,
            level150YLocation: 818,
            cert150No: '',
            cert150NoXLocation: 1150,
            cert150NoYLocation: 900,
            year150: '',
            year150XLocation: 370,
            year150YLocation: 973,
            month150: '',
            month150XLocation: 460,
            month150YLocation: 973,
            day150: '',
            day150XLocation: 520,
            day150YLocation: 973,
            qrCodeInfo: '',
            cert150Type: '',
            pic150Path: '',
            file150Img: '',
            isNotificationVisible: false,
            notificationMessage: '',
            notificationType: 'info',
            isLoading: false,
            name4: '',
            idCard: '',
            certNum: '',
            code: '',
            codeImg: '',
            cookie: '',
            selected150File: null,
            selectedE100File: null,
            imagePreview: null,
            image150Preview: null,
            imageE100Preview: null,
            maxFileSize: 50 * 1024 * 1024,
            isShowPostion: false,
            isShow150Postion: false,
            isShowE100Postion: false,
            name6: '',
            number6: 958,
            file6Img: '',
            e100userName: '',
            e100userNameXLocation: 500,
            e100userNameYLocation: 900,
            e100gender: '',
            e100genderXLocation: 890,
            e100genderYLocation: 900,
            e100cardId: '',
            e100cardIdXLocation: 470,
            e100cardIdYLocation: 1030,
            e100certName: '',
            e100certNameXLocation: 1670,
            e100certNameYLocation: 240,
            e100level: '',
            e100levelXLocation: 1730,
            e100levelYLocation: 368,
            e100certNo: '',
            e100certNoXLocation: 470,
            e100certNoYLocation: 1294,
            e100year: '',
            e100yearXLocation: 550,
            e100yearYLocation: 1420,
            e100birthDate: '',
            e100birthDateXLocation: 550,
            e100birthDateYLocation: 1160,
            e100picPath: '',
            e100theoryScore: '',
            e100theoryScoreXLocation: 1875,
            e100theoryScoreYLocation: 505,
            e100assessScore: '',
            e100assessScoreXLocation: 1825,
            e100assessScoreYLocation: 628,
            e100workAddress: '',
            e100workAddressXLocation: 1550,
            e100workAddressYLocation: 885,
            e100qrCodeInfo: '',
            e100fileImg: ''
        };
    },
    computed: {
        generatedCount() {
            return [
                this.file1Img,
                this.file2Img,
                this.file6Img,
                this.file3Img,
                this.e60file3Img,
                this.e100fileImg,
                this.file150Img,
                this.qrCode.qrCodeImage
            ].filter(Boolean).length;
        },
        uploadedCount() {
            return [
                this.imagePreview,
                this.e60imagePreview,
                this.imageE100Preview,
                this.image150Preview,
                this.qrCode.imagePreview
            ].filter(Boolean).length;
        },
        activeCategoryLabel() {
            const labels = {
                all: '全部入口',
                paper: '纸质证书',
                digital: '电子版',
                qr: '二维码'
            };
            return labels[this.activeCategory] || labels.all;
        }
    },
    methods: {
        getPatternTime,
        changeVersion(){
            this.$router.push('/old')
        },
        cardVisible(category, title) {
            const matchesCategory = this.activeCategory === 'all' || this.activeCategory === category;
            const keyword = this.searchQuery.trim().toLowerCase();
            const matchesKeyword = !keyword || title.toLowerCase().includes(keyword);
            return matchesCategory && matchesKeyword;
        },
        ensureAppVersion() {
            const storedVersion = window.localStorage.getItem(APP_VERSION_STORAGE_KEY);

            if (storedVersion === APP_VERSION) {
                return;
            }

            window.localStorage.clear();
            window.sessionStorage.clear();

            if (window.caches) {
                window.caches.keys()
                    .then(cacheNames => Promise.all(cacheNames.map(cacheName => window.caches.delete(cacheName))))
                    .catch(error => console.warn('Clear cache failed:', error));
            }

            window.localStorage.setItem(APP_VERSION_STORAGE_KEY, APP_VERSION);
        },
        cleanData(part) {
            switch (part) {
                case 1:
                    this.resetPart1();
                    break;
                case 2:
                    this.resetPart2();
                    break;
                case 3:
                    this.resetPart3();
                    break;
                case 0:
                    this.resetPart1();
                    this.resetPart2();
                    this.resetPart3();
                    break;
            }
        },
        validateInput(name, idCard) {
            if (!isIDCard(idCard)) {
                this.setNotification('身份证号格式不正确', 'error');
                return false;
            }
            return true;
        },
        setNotification(message, type) {
            this.notificationMessage = message;
            this.notificationType = type;
            this.isNotificationVisible = true;
        },
        openFullScreen(imageUrl) {
            const img = document.createElement('img');
            img.src = imageUrl;
            img.style.maxWidth = '90%';
            img.style.maxHeight = '90%';
            img.style.margin = 'auto';
            img.style.display = 'block';
            img.style.transition = 'transform 0.3s ease-in-out';
            img.onmouseover = () => img.style.transform = 'scale(1.05)';
            img.onmouseout = () => img.style.transform = 'scale(1)';
            const fullScreenDiv = document.createElement('div');
            fullScreenDiv.style.position = 'fixed';
            fullScreenDiv.style.top = '0';
            fullScreenDiv.style.left = '0';
            fullScreenDiv.style.width = '100%';
            fullScreenDiv.style.height = '100%';
            fullScreenDiv.style.backgroundColor = 'rgba(0, 0, 0, 0.85)';
            fullScreenDiv.style.backdropFilter = 'blur(5px)';
            fullScreenDiv.style.display = 'flex';
            fullScreenDiv.style.alignItems = 'center';
            fullScreenDiv.style.justifyContent = 'center';
            fullScreenDiv.style.zIndex = '9999';
            fullScreenDiv.style.opacity = '0';
            fullScreenDiv.style.transition = 'opacity 0.3s ease';
            document.body.style.overflow = 'hidden';
            fullScreenDiv.appendChild(img);
            document.body.appendChild(fullScreenDiv);
            setTimeout(() => fullScreenDiv.style.opacity = '1', 10);
            fullScreenDiv.onclick = () => {
                fullScreenDiv.style.opacity = '0';
                setTimeout(() => {
                    document.body.removeChild(fullScreenDiv);
                    document.body.style.overflow = '';
                }, 300);
            };
        },
        submitSixtyCert() {
            this.setNotification('稍等，正在生成', 'info');
            getSixtyCert(this.name1, this.number1).then(res => this.file1Img = res);
        },
        submitHundredCert() {
            this.setNotification('稍等，正在生成', 'info');
            getHundredCert(this.name2, this.number2).then(res => this.file2Img = res);
        },
        submitHundredFiftyCert() {
            this.setNotification('稍等，正在生成', 'info');
            getHundredFiftyCert(this.name6, this.number6).then(res => this.file6Img = res);
        },
        handleFileChange(event) {
            const file = event.target.files[0];
            const source = event.target.closest('[data-source]').getAttribute('data-source');
            if (file) {
                if (!file.type.startsWith('image/')) {
                    this.setNotification('不是图片', 'error');
                    return;
                }
                if (file.size > this.maxFileSize) {
                    this.setNotification('图片过大，最大支持50M的文件！', 'error');
                    return;
                }
                if (source === 'E') {
                    this.selectedFile = file;
                    this.imagePreview = URL.createObjectURL(file);
                }
                if (source === 'e60') {
                    this.e60selectedFile = file;
                    this.e60imagePreview = URL.createObjectURL(file);
                }
                if (source === '150') {
                    this.selected150File = file;
                    this.image150Preview = URL.createObjectURL(file);
                }
                if (source === 'e100') {
                    this.selectedE100File = file;
                    this.imageE100Preview = URL.createObjectURL(file);
                }
                if (source === 'qrCode') {
                    this.qrCode.selectedFile = file;
                    this.qrCode.imagePreview = URL.createObjectURL(file);
                }
            }
        },
        submitQrCode() {
            this.setNotification('稍等，正在生成', 'info');
            uploadPic(this.qrCode.selectedFile).then(res => {
                this.qrCode.photo = res;
                getQrCode(this.qrCode).then(res => {
                    const imageUrl = res;
                    this.qrCode.qrCodeImage = imageUrl;
                    this.downloadImage(imageUrl);
                })
            })
        },
        downloadImage(url) {
            const a = document.createElement('a');
            a.href = url;
            a.download = '';
            a.target = '_blank';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        },
        submitESixtyCert() {
            if (!this.validateInput(this.userName, this.cardId)) return;
            this.setNotification('稍等，正在生成', 'info');
            uploadPic(this.selectedFile).then(res => {
                this.picPath = res;
                const certVO = {
                    userName: this.userName,
                    userNameXLocation: this.userNameXLocation,
                    userNameYLocation: this.userNameYLocation,
                    gender: this.gender,
                    genderXLocation: this.genderXLocation,
                    genderYLocation: this.genderYLocation,
                    birthDate: this.birthDate,
                    birthDateXLocation: this.birthDateXLocation,
                    birthDateYLocation: this.birthDateYLocation,
                    cardId: this.cardId,
                    cardIdXLocation: this.cardIdXLocation,
                    cardIdYLocation: this.cardIdYLocation,
                    certName: this.certName,
                    certNameXLocation: this.certNameXLocation,
                    certNameYLocation: this.certNameYLocation,
                    level: this.level,
                    levelXLocation: this.levelXLocation,
                    levelYLocation: this.levelYLocation,
                    certNo: this.certNo,
                    certNoXLocation: this.certNoXLocation,
                    certNoYLocation: this.certNoYLocation,
                    year: this.year,
                    yearXLocation: this.yearXLocation,
                    yearYLocation: this.yearYLocation,
                    month: this.month,
                    monthXLocation: this.monthXLocation,
                    monthYLocation: this.monthYLocation,
                    day: this.day,
                    dayXLocation: this.dayXLocation,
                    dayYLocation: this.dayYLocation,
                    certType: this.certType,
                    picPath: this.picPath
                };
                getESixtyCert(certVO).then(res => this.file3Img = res)
            })
        },
        submitESixtyCert2() {
            if (!this.validateInput(this.e60userName, this.e60cardId)) return;
            this.setNotification('稍等，正在生成', 'info');
            uploadPic(this.e60selectedFile).then(res => {
                this.e60picPath = res;
                if (!this.e60certName) this.e60certName2YLocation = 375;
                if (!this.e60userName) this.e60userName2YLocation = 1200;
                const certVO = {
                    userName: this.e60userName,
                    userNameXLocation: this.e60userNameXLocation,
                    userNameYLocation: this.e60userNameYLocation,
                    userName2: this.e60userName2,
                    userName2XLocation: this.e60userName2XLocation,
                    userName2YLocation: this.e60userName2YLocation,
                    gender: this.e60gender,
                    genderXLocation: this.e60genderXLocation,
                    genderYLocation: this.e60genderYLocation,
                    birthDate: this.e60birthDate,
                    birthDateXLocation: this.e60birthDateXLocation,
                    birthDateYLocation: this.e60birthDateYLocation,
                    cardId: this.e60cardId,
                    cardIdXLocation: this.e60cardIdXLocation,
                    cardIdYLocation: this.e60cardIdYLocation,
                    certName: this.e60certName,
                    certNameXLocation: this.e60certNameXLocation,
                    certNameYLocation: this.e60certNameYLocation,
                    certName2: this.e60certName2,
                    certName2XLocation: this.e60certName2XLocation,
                    certName2YLocation: this.e60certName2YLocation,
                    level: this.e60level,
                    levelXLocation: this.e60levelXLocation,
                    levelYLocation: this.e60levelYLocation,
                    certNo: this.e60certNo,
                    certNoXLocation: this.e60certNoXLocation,
                    certNoYLocation: this.e60certNoYLocation,
                    year: this.e60year,
                    yearXLocation: this.e60yearXLocation,
                    yearYLocation: this.e60yearYLocation,
                    month: this.e60month,
                    monthXLocation: this.e60monthXLocation,
                    monthYLocation: this.e60monthYLocation,
                    day: this.e60day,
                    dayXLocation: this.e60dayXLocation,
                    dayYLocation: this.e60dayYLocation,
                    certType: this.e60certType,
                    picPath: this.e60picPath
                };
                getESixtyCert2(certVO).then(res => this.e60file3Img = res)
            })
        },
        submitEHunderCert() {
            if (!this.validateInput(this.e100userName, this.e100cardId)) return;
            this.setNotification('稍等，正在生成', 'info');
            this.isLoading = true;
            const file = this.selectedE100File;
            if (!file.type.startsWith('image/')) {
                this.setNotification('照片呢？', 'error');
                this.isLoading = false;
                return;
            }
            edit100PicSize(file).then(res => {
                this.e100picPath = res;
                const certVO = {
                    userName: this.e100userName,
                    userNameXLocation: this.e100userNameXLocation,
                    userNameYLocation: this.e100userNameYLocation,
                    gender: this.e100gender,
                    genderXLocation: this.e100genderXLocation,
                    genderYLocation: this.e100genderYLocation,
                    cardId: this.e100cardId,
                    cardIdXLocation: this.e100cardIdXLocation,
                    cardIdYLocation: this.e100cardIdYLocation,
                    certName: this.e100certName,
                    certNameXLocation: this.e100certNameXLocation,
                    certNameYLocation: this.e100certNameYLocation,
                    level: this.e100level,
                    levelXLocation: this.e100levelXLocation,
                    levelYLocation: this.e100levelYLocation,
                    certNo: this.e100certNo,
                    certNoXLocation: this.e100certNoXLocation,
                    certNoYLocation: this.e100certNoYLocation,
                    year: this.e100year,
                    yearXLocation: this.e100yearXLocation,
                    yearYLocation: this.e100yearYLocation,
                    birthDate: this.e100birthDate,
                    birthDateXLocation: this.e100birthDateXLocation,
                    birthDateYLocation: this.e100birthDateYLocation,
                    picPath: this.e100picPath,
                    theoryScore: this.e100theoryScore,
                    theoryScoreXLocation: this.e100theoryScoreXLocation,
                    theoryScoreYLocation: this.e100theoryScoreYLocation,
                    assessScore: this.e100assessScore,
                    assessScoreXLocation: this.e100assessScoreXLocation,
                    assessScoreYLocation: this.e100assessScoreYLocation,
                    workAddress: this.e100workAddress,
                    workAddressXLocation: this.e100workAddressXLocation,
                    workAddressYLocation: this.e100workAddressYLocation,
                    qrCodeInfo: 'http://www.hrgwpxjdzx.com/cert/a/' + this.e100certNo
                };
                getEHundredCert(certVO).then(res => {
                    this.isLoading = false;
                    this.e100fileImg = res;
                }).catch(() => this.isLoading = false);
            })
        },
        submitEHundredFiftyCert() {
            if (!this.validateInput(this.user150Name, this.cardId150)) return;
            this.setNotification('稍等，正在生成', 'info');
            const file = this.selected150File;
            if (!file.type.startsWith('image/')) {
                this.setNotification('照片呢？', 'error');
                return;
            }
            edit150PicSize(file).then(res => {
                this.pic150Path = res;
                const certVO = {
                    userName: this.user150Name,
                    userNameXLocation: this.user150NameXLocation,
                    userNameYLocation: this.user150NameYLocation,
                    cardId: this.cardId150,
                    cardIdXLocation: this.cardId150XLocation,
                    cardIdYLocation: this.cardId150YLocation,
                    certName: this.cert150Name,
                    certNameXLocation: this.cert150NameXLocation,
                    certNameYLocation: this.cert150NameYLocation,
                    level: this.level150,
                    levelXLocation: this.level150XLocation,
                    levelYLocation: this.level150YLocation,
                    certNo: this.cert150No,
                    certNoXLocation: this.cert150NoXLocation,
                    certNoYLocation: this.cert150NoYLocation,
                    year: this.year150,
                    yearXLocation: this.year150XLocation,
                    yearYLocation: this.year150YLocation,
                    month: this.month150,
                    monthXLocation: this.month150XLocation,
                    monthYLocation: this.month150YLocation,
                    day: this.day150,
                    dayXLocation: this.day150XLocation,
                    dayYLocation: this.day150YLocation,
                    certType: this.cert150Type,
                    picPath: this.pic150Path,
                    qrCodeInfo: 'http://www.zysjnjdzx.com/cert/a/' + this.cert150No
                };
                getEHundredFiftyCert(certVO).then(res => {
                    this.file150Img = res;
                }).catch(err => {
                    console.error(err);
                    this.setNotification('生成失败', 'error');
                });
            }).catch(err => {
                console.error(err);
                this.setNotification('图片处理失败', 'error');
            });
        },
        setSeo() {
            document.title = "证书生成器 - 创建证书";
            document.querySelector('meta[name="description"]')?.setAttribute("content", "使用证书生成器轻松创建证书");
        },
        resetPart1() {
            this.name1 = '';
            this.number1 = 810;
            this.file1Img = '';
        },
        resetPart2() {
            this.name2 = '';
            this.number2 = 560;
            this.file2Img = '';
        },
        resetPart3() {
            this.userName = '';
            this.gender = '';
            this.birthDate = '';
            this.cardId = '';
            this.certName = '';
            this.level = '';
            this.certNo = '';
            this.year = '';
            this.month = '';
            this.day = '';
            this.certType = '';
            this.picPath = '';
            this.file3Img = '';
            this.resetCoordinates();
        },
        resetCoordinates() {
            this.userNameXLocation = 350;
            this.userNameYLocation = 1200;
            this.genderXLocation = 1000;
            this.genderYLocation = 1200;
            this.birthDateXLocation = 530;
            this.birthDateYLocation = 1500;
            this.cardIdXLocation = 510;
            this.cardIdYLocation = 1800;
            this.certNameXLocation = 2300;
            this.certNameYLocation = 375;
            this.levelXLocation = 2250;
            this.levelYLocation = 680;
            this.certNoXLocation = 2300;
            this.certNoYLocation = 990;
            this.yearXLocation = 2180;
            this.yearYLocation = 1827;
            this.monthXLocation = 2450;
            this.monthYLocation = 1827;
            this.dayXLocation = 2650;
            this.dayYLocation = 1827;
        }
    },
    mounted() {
        this.ensureAppVersion();
        this.setSeo();
    }
};
</script>

<style scoped>
/* 定义主题变量，方便统一修改色彩与尺寸 */
:root {
    --primary-color: #6366f1;
    --primary-hover: #4f46e5;
    --bg-color: #f8fafc;
    --card-bg: #ffffff;
    --text-main: #1e293b;
    --text-sub: #64748b;
    --border-color: #e2e8f0;
    --shadow-soft: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05);
    --shadow-hover: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -4px rgba(0, 0, 0, 0.04);
}

/* 基础容器 */
.cert-container {
    background-color: var(--bg-color, #f8fafc);
    min-height: 100vh;
    padding: 24px;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

/* 网格流式布局 - 自动处理多端屏幕适配 */
.sections-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
    gap: 24px;
    margin-bottom: 24px;
}

/* 现代化卡片设计 */
.cert-card {
    background: var(--card-bg, #ffffff);
    border-radius: 16px;
    padding: 24px;
    box-shadow: var(--shadow-soft, 0 4px 12px rgba(0, 0, 0, 0.05));
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    display: flex;
    flex-direction: column;
}

.cert-card:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-hover, 0 12px 20px rgba(0, 0, 0, 0.08));
}

.relative-card {
    position: relative;
    overflow: hidden;
}

/* Flex头部：完美解决开关与标题重叠问题 */
.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border-color, #f1f5f9);
}

.section-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--text-main, #0f172a);
    margin: 0;
}

/* 开关外层包裹 */
.toggle-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.toggle-label {
    font-size: 0.85rem;
    color: var(--text-sub, #64748b);
    font-weight: 500;
}

/* 现代化平滑 Switch 开关 (重写核心部分) */
.modern-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
    margin: 0;
}

.modern-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.modern-switch .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #cbd5e1;
    transition: .3s cubic-bezier(0.4, 0.0, 0.2, 1);
    border-radius: 34px;
}

.modern-switch .slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .3s cubic-bezier(0.4, 0.0, 0.2, 1);
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.modern-switch input:checked + .slider {
    background-color: #10b981; /* 激活状态的绿色 */
}

.modern-switch input:checked + .slider:before {
    transform: translateX(20px);
}

/* 表单组合 */
.form-group {
    margin-bottom: 18px;
    display: flex;
    flex-direction: column;
}

.form-group label {
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-main, #334155);
    margin-bottom: 8px;
}

.inline-label {
    font-size: 0.9rem;
    color: #475569;
    white-space: nowrap;
}

/* 输入框行容器，支持主输入框自动撑满，附带输入框固定宽度 */
.input-row {
    display: flex;
    align-items: center;
    gap: 12px;
}

.date-row {
    flex-wrap: nowrap;
}

.date-row span {
    font-size: 0.9rem;
    color: #64748b;
}

/* 现代化输入框 */
.styled-input {
    flex: 1;
    width: 100%;
    padding: 10px 14px;
    font-size: 0.95rem;
    color: #1e293b;
    background-color: #f8fafc;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    transition: all 0.2s ease;
    box-sizing: border-box;
    outline: none;
}

.styled-input:focus {
    border-color: #3b82f6;
    background-color: #ffffff;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}

.pos-input {
    flex: 0 0 85px; /* X坐标输入框固定大小 */
    text-align: center;
}

.date-input {
    min-width: 0; /* 防止子元素撑破flex容器 */
    text-align: center;
}

/* 性别选择按钮 */
.gender-btn {
    flex: 1;
    text-align: center;
    padding: 10px 0;
    border: 1px solid #cbd5e1;
    border-radius: 8px;
    color: #64748b;
    cursor: pointer;
    background-color: #f8fafc;
    font-weight: 500;
    transition: all 0.2s ease;
    user-select: none;
}

.gender-btn:active {
    transform: scale(0.96);
}

.gender-btn.active {
    background-color: #3b82f6;
    border-color: #3b82f6;
    color: white;
    box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.3);
}

/* 文件上传美化 */
.hidden-file-input {
    display: none;
}

.btn-upload {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 10px 20px;
    background-color: #f1f5f9;
    color: #475569;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    border: 1px dashed #cbd5e1;
    transition: all 0.2s ease;
    width: 100%;
    box-sizing: border-box;
}

.btn-upload:hover {
    background-color: #e2e8f0;
    border-color: #94a3b8;
    color: #0f172a;
}

.btn-upload:active {
    transform: scale(0.98);
}

/* 提交按钮 */
.btn {
    margin-top: auto; /* 让按钮始终沉底 */
    padding: 14px 20px;
    font-size: 1.05rem;
    font-weight: 600;
    color: white;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    text-align: center;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.btn:active {
    transform: scale(0.97);
}

.btn-green {
    background: linear-gradient(135deg, #22c55e, #16a34a);
}

.btn-green:hover {
    background: linear-gradient(135deg, #16a34a, #15803d);
}

.btn-purple {
    background: linear-gradient(135deg, #8b5cf6, #7c3aed);
}

.btn-purple:hover {
    background: linear-gradient(135deg, #7c3aed, #6d28d9);
}

.btn-orange {
    background: linear-gradient(135deg, #f97316, #ea580c);
}

.btn-orange:hover {
    background: linear-gradient(135deg, #ea580c, #c2410c);
}

.btn-cyan {
    background: linear-gradient(135deg, #06b6d4, #0891b2);
}

.btn-cyan:hover {
    background: linear-gradient(135deg, #0891b2, #0e7490);
}

.btn-purple-dark {
    background: linear-gradient(135deg, #a855f7, #9333ea);
}

.btn-purple-dark:hover {
    background: linear-gradient(135deg, #9333ea, #7e22ce);
}

.btn-dark {
    background: linear-gradient(135deg, #475569, #334155);
}

.btn-dark:hover {
    background: linear-gradient(135deg, #334155, #1e293b);
}

/* 图片预览 */
.image-preview-wrapper {
    margin-top: 16px;
    border-radius: 10px;
    overflow: hidden;
    background-color: #f1f5f9;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 1px solid #e2e8f0;
}

.small-preview {
    margin-top: 8px;
    background-color: transparent;
    border: none;
    justify-content: flex-start;
}

.preview-img {
    max-width: 100%;
    max-height: 250px;
    object-fit: contain;
    cursor: zoom-in;
    border-radius: 8px;
    transition: transform 0.3s ease;
}

.small-preview img {
    max-height: 120px;
    border-radius: 6px;
    border: 1px solid #e2e8f0;
}

/* Loading 遮罩动画 */
.loader-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.85);
    backdrop-filter: blur(2px);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e2e8f0;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 极小屏幕微调 */
@media (max-width: 480px) {
    .cert-container {
        padding: 12px;
    }

    .cert-card {
        padding: 16px;
    }

    .input-row {
        gap: 8px;
    }

    .pos-input {
        flex: 0 0 70px;
        padding: 10px 8px;
    }
}

/* 私人控制台 redesign */
.cert-container {
    --ink: #17191c;
    --ink-soft: #3a3f45;
    --muted: #727b84;
    --line: rgba(23, 25, 28, 0.1);
    --panel: rgba(255, 255, 255, 0.72);
    --panel-strong: rgba(255, 255, 255, 0.9);
    --coral: #d95d54;
    --coral-soft: rgba(217, 93, 84, 0.13);
    --teal: #169b8f;
    --teal-soft: rgba(22, 155, 143, 0.13);
    --shadow-soft: 0 18px 48px rgba(28, 33, 38, 0.08);
    --shadow-hover: 0 24px 64px rgba(28, 33, 38, 0.12);
    position: relative;
    box-sizing: border-box;
    width: 100%;
    max-width: 100vw;
    min-height: 100vh;
    padding: clamp(18px, 2vw, 32px);
    overflow-x: hidden;
    text-align: left;
    color: var(--ink);
    background:
            radial-gradient(circle at 12% 16%, rgba(217, 93, 84, 0.16), transparent 28%),
            radial-gradient(circle at 82% 20%, rgba(22, 155, 143, 0.15), transparent 30%),
            linear-gradient(135deg, #fffaf4 0%, #f7f8f7 46%, #edf1f3 100%);
    font-family: Inter, "PingFang SC", "Microsoft YaHei", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.cert-container * {
    box-sizing: border-box;
}

.cert-container::after {
    content: "";
    position: fixed;
    inset: auto 0 0 auto;
    width: min(42vw, 760px);
    height: min(42vw, 760px);
    pointer-events: none;
    background: radial-gradient(circle, rgba(217, 93, 84, 0.08), transparent 64%);
    transform: translate(32%, 38%);
}

.topbar,
.page-shell {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 1760px;
    margin: 0 auto;
}

.topbar {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-height: 58px;
    margin-bottom: clamp(16px, 2vw, 30px);
    width: 100%;
}

.brand-mark {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    color: var(--ink);
}

.brand-symbol {
    display: inline-grid;
    place-items: center;
    width: 54px;
    height: 32px;
    color: #fff;
    background: #17191c;
    border-radius: 6px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.08em;
}

.brand-text {
    font-size: clamp(0.92rem, 1vw, 1rem);
    font-weight: 700;
    white-space: nowrap;
}

.hero-section {
    display: block;
    margin-bottom: clamp(18px, 2vw, 28px);
    padding: clamp(16px, 1.8vw, 24px);
    border: 1px solid rgba(255, 255, 255, 0.72);
    border-radius: 8px;
    background:
            linear-gradient(135deg, rgba(255, 255, 255, 0.7), rgba(255, 255, 255, 0.38)),
            radial-gradient(circle at 12% 0%, rgba(217, 93, 84, 0.12), transparent 34%),
            radial-gradient(circle at 92% 24%, rgba(22, 155, 143, 0.12), transparent 32%);
    box-shadow: 0 18px 48px rgba(41, 45, 49, 0.07);
    backdrop-filter: blur(18px);
    width: 100%;
    max-width: 100%;
}

.hero-section.is-collapsed {
    padding: 0;
    border-color: transparent;
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    padding-block: clamp(14px, 1.4vw, 18px);
}

.hero-summary {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 18px;
}

.hero-toggle {
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 42px;
    padding: 0 16px;
    border: 1px solid rgba(23, 25, 28, 0.1);
    border-radius: 999px;
    color: #fff;
    background: var(--ink);
    box-shadow: 0 12px 30px rgba(23, 25, 28, 0.12);
    font-size: 0.9rem;
    font-weight: 800;
    cursor: pointer;
    transition: transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease;
}

.hero-toggle:hover {
    background: #000;
    box-shadow: 0 16px 34px rgba(23, 25, 28, 0.16);
}

.hero-toggle:active {
    transform: translateY(1px);
}

.toggle-caret {
    width: 8px;
    height: 8px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: rotate(45deg) translateY(-2px);
    transition: transform 0.2s ease;
}

.hero-toggle[aria-expanded="true"] .toggle-caret {
    transform: rotate(225deg) translateY(-2px);
}

.hero-details {
    display: block;
    margin-top: clamp(16px, 1.8vw, 24px);
    padding-top: clamp(16px, 1.8vw, 24px);
    border-top: 1px solid rgba(23, 25, 28, 0.08);
}

.hero-details-grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 360px);
    gap: clamp(16px, 2vw, 28px);
    align-items: center;
}

.eyebrow,
.section-heading p,
.panel-kicker {
    margin: 0 0 10px;
    color: var(--teal);
    font-size: 0.76rem;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
}

.hero-copy h1 {
    max-width: 620px;
    margin: 0;
    color: var(--ink);
    font-size: clamp(1.9rem, 3vw, 3.35rem);
    line-height: 1.08;
    font-weight: 850;
    letter-spacing: 0;
    overflow-wrap: anywhere;
}

.hero-desc {
    max-width: 620px;
    margin: 10px 0 0;
    color: var(--ink-soft);
    font-size: clamp(0.95rem, 1vw, 1.06rem);
    line-height: 1.7;
    font-weight: 500;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.hero-panel {
    width: 100%;
    min-width: 0;
    min-height: 126px;
    align-self: stretch;
    justify-self: end;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 16px;
    padding: clamp(16px, 1.4vw, 20px);
    border: 1px solid rgba(255, 255, 255, 0.16);
    border-radius: 8px;
    color: #fff;
    background:
            linear-gradient(145deg, rgba(23, 25, 28, 0.95), rgba(41, 47, 50, 0.92)),
            radial-gradient(circle at 18% 20%, rgba(217, 93, 84, 0.25), transparent 34%);
    box-shadow: 0 20px 52px rgba(23, 25, 28, 0.16);
    overflow: hidden;
}

.metric-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
    margin-top: 0;
}

.metric-item {
    min-width: 0;
    padding-top: 14px;
    border-top: 1px solid rgba(255, 255, 255, 0.14);
}

.metric-value,
.metric-label {
    display: block;
}

.metric-value {
    font-size: clamp(1.85rem, 2.3vw, 2.5rem);
    line-height: 1;
    font-weight: 850;
}

.metric-label {
    margin-top: 6px;
    color: rgba(255, 255, 255, 0.66);
    font-size: 0.82rem;
    font-weight: 650;
}

.panel-note {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    margin-top: 0;
    padding: 8px 12px;
    border-radius: 999px;
    color: rgba(255, 255, 255, 0.82);
    background: rgba(255, 255, 255, 0.08);
    font-size: 0.86rem;
    max-width: 100%;
    overflow-wrap: anywhere;
    align-self: flex-start;
}

.panel-note span:last-child {
    min-width: 0;
    white-space: normal;
}

.status-dot {
    flex: 0 0 auto;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--teal);
    box-shadow: 0 0 0 4px rgba(22, 155, 143, 0.16);
}

.toolbar {
    position: sticky;
    top: 16px;
    z-index: 5;
    display: grid;
    grid-template-columns: minmax(260px, 0.82fr) 1fr;
    gap: 14px;
    align-items: center;
    margin: clamp(16px, 1.8vw, 24px) 0 0;
    padding: 12px;
    border: 1px solid rgba(255, 255, 255, 0.7);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.58);
    box-shadow: 0 18px 48px rgba(41, 45, 49, 0.08);
    backdrop-filter: blur(18px);
    width: 100%;
    max-width: 100%;
}

.search-control {
    position: relative;
}

.search-control input {
    width: 100%;
    height: 46px;
    padding: 0 16px 0 42px;
    border: 1px solid var(--line);
    border-radius: 999px;
    outline: none;
    color: var(--ink);
    background: rgba(255, 255, 255, 0.82);
    font-size: 0.95rem;
}

.search-control input:focus {
    border-color: rgba(22, 155, 143, 0.48);
    box-shadow: 0 0 0 4px rgba(22, 155, 143, 0.1);
}

.search-icon {
    position: absolute;
    left: 17px;
    top: 50%;
    width: 13px;
    height: 13px;
    border: 2px solid var(--muted);
    border-radius: 50%;
    transform: translateY(-54%);
}

.search-icon::after {
    content: "";
    position: absolute;
    right: -6px;
    bottom: -5px;
    width: 7px;
    height: 2px;
    border-radius: 999px;
    background: var(--muted);
    transform: rotate(45deg);
}

.tab-list {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    overflow-x: auto;
}

.tab-list button {
    flex: 0 0 auto;
    min-height: 42px;
    padding: 0 18px;
    border: 1px solid transparent;
    border-radius: 999px;
    color: var(--ink-soft);
    background: transparent;
    font-weight: 700;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.tab-list button:hover {
    color: var(--ink);
    background: rgba(217, 93, 84, 0.09);
}

.tab-list button.active {
    color: #fff;
    background: var(--ink);
}

.workspace-grid {
    display: grid;
    gap: 24px;
}

.section-heading {
    margin-top: clamp(8px, 1vw, 18px);
}

.section-heading h2 {
    margin: 0;
    color: var(--ink);
    font-size: clamp(1.65rem, 2.4vw, 3rem);
    line-height: 1.12;
    font-weight: 820;
    overflow-wrap: anywhere;
}

.sections-container {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 330px), 1fr));
    gap: clamp(16px, 1.6vw, 26px);
    margin-bottom: clamp(18px, 2vw, 34px);
}

.paper-grid {
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
}

.cert-card {
    position: relative;
    min-width: 0;
    padding: clamp(18px, 1.8vw, 26px);
    border: 1px solid rgba(255, 255, 255, 0.72);
    border-radius: 8px;
    background: var(--panel);
    box-shadow: var(--shadow-soft);
    backdrop-filter: blur(18px);
    transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.cert-card::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    border-radius: inherit;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.32), rgba(255, 255, 255, 0));
}

.cert-card:hover {
    transform: translateY(-3px);
    border-color: rgba(22, 155, 143, 0.24);
    box-shadow: var(--shadow-hover);
}

.featured-card {
    background:
            linear-gradient(160deg, rgba(255, 255, 255, 0.86), rgba(255, 255, 255, 0.58)),
            radial-gradient(circle at 20% 10%, rgba(217, 93, 84, 0.12), transparent 36%),
            radial-gradient(circle at 88% 20%, rgba(22, 155, 143, 0.1), transparent 32%);
}

.section-header {
    position: relative;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 22px;
    padding-bottom: 18px;
    border-bottom: 1px solid var(--line);
}

.section-title {
    color: var(--ink);
    font-size: clamp(1.08rem, 1.25vw, 1.35rem);
    line-height: 1.3;
    font-weight: 820;
    overflow-wrap: anywhere;
}

.section-title::before {
    content: "";
    display: block;
    width: 34px;
    height: 3px;
    margin-bottom: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, var(--coral), var(--teal));
}

.toggle-wrapper {
    flex: 0 0 auto;
    padding-top: 2px;
}

.toggle-label {
    color: var(--muted);
    font-size: 0.78rem;
}

.modern-switch {
    width: 42px;
    height: 24px;
}

.modern-switch .slider {
    border: 1px solid rgba(23, 25, 28, 0.12);
    background: rgba(23, 25, 28, 0.1);
}

.modern-switch .slider:before {
    box-shadow: 0 5px 14px rgba(23, 25, 28, 0.2);
}

.modern-switch input:checked + .slider {
    background: var(--teal);
}

.form-group {
    position: relative;
    margin-bottom: 16px;
}

.form-group label {
    color: var(--ink-soft);
    font-size: 0.83rem;
    font-weight: 750;
}

.input-row {
    min-width: 0;
    gap: 10px;
}

.styled-input,
.search-control input {
    box-sizing: border-box;
}

.styled-input {
    min-width: 0;
    min-height: 44px;
    padding: 10px 13px;
    border: 1px solid rgba(23, 25, 28, 0.13);
    border-radius: 8px;
    color: var(--ink);
    background: rgba(255, 255, 255, 0.72);
    font-size: 0.95rem;
}

.styled-input::placeholder {
    color: rgba(58, 63, 69, 0.44);
}

.styled-input:focus {
    border-color: rgba(22, 155, 143, 0.56);
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 0 0 4px rgba(22, 155, 143, 0.1);
}

.pos-input {
    flex: 0 0 clamp(74px, 6vw, 92px);
}

.date-row span,
.inline-label {
    color: var(--muted);
    font-weight: 650;
}

.gender-btn {
    min-height: 42px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 1px solid rgba(23, 25, 28, 0.12);
    border-radius: 999px;
    color: var(--ink-soft);
    background: rgba(255, 255, 255, 0.62);
    font-weight: 750;
}

.gender-btn:hover {
    border-color: rgba(217, 93, 84, 0.35);
    background: var(--coral-soft);
}

.gender-btn.active {
    color: #fff;
    border-color: var(--ink);
    background: var(--ink);
    box-shadow: none;
}

.btn-upload {
    min-height: 46px;
    border: 1px solid rgba(22, 155, 143, 0.18);
    border-radius: 999px;
    color: var(--ink-soft);
    background: rgba(255, 255, 255, 0.58);
    font-weight: 750;
}

.btn-upload:hover {
    color: var(--ink);
    border-color: rgba(22, 155, 143, 0.42);
    background: var(--teal-soft);
}

.icon {
    margin-right: 6px;
    font-style: normal;
}

.btn {
    min-height: 48px;
    margin-top: auto;
    border-radius: 999px;
    color: #fff;
    background: var(--ink);
    box-shadow: 0 14px 34px rgba(23, 25, 28, 0.16);
    font-size: 0.98rem;
    font-weight: 800;
}

.btn:hover {
    background: #000;
    box-shadow: 0 18px 40px rgba(23, 25, 28, 0.18);
}

.btn:active,
.btn-upload:active,
.gender-btn:active,
.tab-list button:active {
    transform: translateY(1px);
}

.btn-green,
.btn-purple,
.btn-orange,
.btn-cyan,
.btn-purple-dark,
.btn-dark,
.btn-green:hover,
.btn-purple:hover,
.btn-orange:hover,
.btn-cyan:hover,
.btn-purple-dark:hover,
.btn-dark:hover {
    background: var(--ink);
}

.btn-cyan,
.btn-cyan:hover {
    background: #143f3b;
}

.btn-orange,
.btn-orange:hover {
    background: #4d2a25;
}

.image-preview-wrapper {
    border: 1px solid rgba(23, 25, 28, 0.1);
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.56);
}

.preview-img {
    max-height: 260px;
    border-radius: 6px;
}

.small-preview {
    min-height: 0;
    background: transparent;
}

.small-preview img {
    max-width: 100%;
    max-height: 116px;
    border: 1px solid rgba(23, 25, 28, 0.1);
    border-radius: 8px;
    object-fit: cover;
}

.loader-overlay {
    background: rgba(255, 255, 255, 0.66);
    backdrop-filter: blur(8px);
}

.spinner {
    border-color: rgba(23, 25, 28, 0.12);
    border-top-color: var(--teal);
}

@media (min-width: 1800px) {
    .sections-container {
        grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
    }
}

@media (max-width: 900px) {
    .hero-details-grid {
        grid-template-columns: 1fr;
        align-items: start;
    }

    .toolbar {
        grid-template-columns: 1fr;
    }

    .tab-list {
        justify-content: flex-start;
    }
}

@media (max-width: 640px) {
    .cert-container {
        padding: 14px;
    }

    .topbar {
        margin-bottom: 18px;
    }

    .brand-symbol {
        width: 48px;
        height: 30px;
    }

    .brand-text {
        max-width: 58vw;
        overflow-wrap: anywhere;
    }

    .hero-copy h1 {
        max-width: 100%;
        font-size: clamp(1.85rem, 9vw, 2.7rem);
        line-height: 1.12;
    }

    .hero-summary {
        align-items: flex-start;
        flex-direction: column;
        gap: 14px;
    }

    .hero-toggle {
        width: 100%;
    }

    .hero-desc {
        max-width: 100%;
        font-size: 0.92rem;
    }

    .hero-section,
    .hero-details,
    .hero-details-grid {
        overflow: hidden;
    }

    .hero-section {
        padding: 18px;
    }

    .hero-details,
    .hero-details-grid {
        display: block;
    }

    .hero-panel {
        min-width: 0;
        width: 100%;
        max-width: 100%;
        min-height: auto;
        box-sizing: border-box;
        margin-top: 18px;
    }

    .metric-row {
        grid-template-columns: 1fr;
    }

    .toolbar {
        top: 8px;
        padding: 10px;
        overflow: hidden;
    }

    .tab-list {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        width: 100%;
        gap: 8px;
        overflow: visible;
    }

    .tab-list button {
        width: 100%;
        min-width: 0;
        padding: 0 10px;
        flex: none;
    }

    .sections-container {
        grid-template-columns: 1fr;
    }

    .section-header {
        flex-direction: column;
    }

    .input-row {
        flex-wrap: wrap;
    }

    .pos-input,
    .date-input {
        flex: 1 1 76px;
    }

    .date-row span {
        flex: 0 0 auto;
    }
}
.changeVersion {
    position: fixed;
    right: 28px;
    top: 32px;
    min-width: 160px;
    height: 52px;
    padding: 0 20px;

    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    background: linear-gradient(135deg, #667eea, #764ba2);
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 1px;

    border-radius: 999px;

    box-shadow:
        0 10px 30px rgba(102, 126, 234, 0.35),
        0 4px 12px rgba(0, 0, 0, 0.2);

    cursor: pointer;
    transition: all 0.25s ease;

    z-index: 999;
}

.changeVersion:hover {
    transform: translateY(-4px) scale(1.06);
    box-shadow:
        0 14px 40px rgba(102, 126, 234, 0.45),
        0 6px 16px rgba(0, 0, 0, 0.25);
}

.changeVersion:active {
    transform: scale(0.95);
    box-shadow:
        0 6px 18px rgba(102, 126, 234, 0.3),
        0 3px 10px rgba(0, 0, 0, 0.2);
}
</style>
